/**
 * §C Offers & pricing facts — explicit-save buffer (bb-phase2-explicit-save-brief-20260810).
 *
 * OffersPanel used to write every keystroke's blur straight to `bb_offers`
 * through `upsertEntry`. Under the explicit-save contract those edits buffer
 * here instead and commit as a batch when the section's Save button fires.
 *
 * Three deliberate properties, each one a landmine the save-lane briefs name:
 *
 * 1. **Deletes are id-keyed, never synthesized from a list diff.** Removing a
 *    row records THAT row's id; nothing ever compares "the list before" to
 *    "the list after" and infers what vanished. (§B's prod never_say loss was
 *    a mis-targeted per-row delete; the design conclusion — id-keyed ops only
 *    — applies to every converted section, this one included.)
 * 2. **Create-then-edit collapses into ONE create.** A buffered new row
 *    accumulates its fields locally and reaches the server once, fully
 *    populated. This is also the fix for §C's standing empty-row defect: the
 *    old `+ Add guarantee` wrote `{verbatim_text: ''}` immediately, minting a
 *    blank row that satisfied the completeness gate while giving agents
 *    nothing to quote. A blank buffered create is not a change (it does not
 *    count, does not enable Save) and is never committed.
 * 3. **Patches are per-field and diffed against what is stored.** Typing a
 *    value and typing it back drops the field from the buffer, so
 *    `changeCount()` is the real number of pending edits rather than a
 *    keystroke tally — and `save()` sends only fields the operator actually
 *    changed, never a whole-row blind write over a concurrent update.
 *
 * Verify / unverify / approved-for-public stay IMMEDIATE in the panel: they
 * are operator acts on a persisted row, not content edits, and the DB CHECK
 * `bb_offers_verified_gate` couples approval to a `verified_by` that only a
 * real (already-saved) row can have.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BbEntry, OfferKind } from '@/features/brand-bible-wizard/types';
import type { DirtyFormSaveResult } from './save';

/** Field-level edits pending against one row. */
export type OfferPatch = Record<string, unknown>;

export interface BufferedCreate {
  /** Client-side row identity — never sent to the server. */
  tempId: string;
  kind: OfferKind;
  payload: OfferPatch;
}

export interface OffersBuffer {
  /** entry id -> the fields changed on it */
  patches: Record<string, OfferPatch>;
  creates: BufferedCreate[];
  /** entry ids marked for soft-delete, recorded at click time */
  deletes: string[];
}

export const EMPTY_OFFERS_BUFFER: OffersBuffer = { patches: {}, creates: [], deletes: [] };

/** A row as the panel renders it: the stored entry merged with its buffered
 *  edits, or a buffered create that has no server row yet (`isPending`). */
export type OfferRow = BbEntry<'offers'> & { isPending: boolean };

/**
 * Empty-equivalence: `''`, `null` and `undefined` are the same absence, so
 * clearing a never-set field is not an edit. Arrays compare structurally
 * (promotion `channels`), everything else by identity.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  }
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty || bEmpty) return aEmpty && bEmpty;
  return a === b;
}

/** True when nothing in the payload carries content — the state a fresh
 *  "+ Add …" row starts in, and the state that must never reach the server. */
export function isBlankPayload(payload: OfferPatch): boolean {
  return Object.values(payload).every((v) => sameValue(v, null));
}

/** Pending edits, counted the way the footer button reports them:
 *  one per changed field, one per non-blank new row, one per removal. */
export function countChanges(buffer: OffersBuffer): number {
  const patched = Object.values(buffer.patches).reduce((n, p) => n + Object.keys(p).length, 0);
  const created = buffer.creates.filter((c) => !isBlankPayload(c.payload)).length;
  return patched + created + buffer.deletes.length;
}

/** Stored rows (minus buffered removals, plus buffered edits), then new rows. */
export function mergeRows(entries: BbEntry<'offers'>[], buffer: OffersBuffer): OfferRow[] {
  const kept = entries
    .filter((e) => !buffer.deletes.includes(e.id))
    .map((e) => ({ ...e, ...(buffer.patches[e.id] ?? {}), isPending: false }) as OfferRow);

  const added = buffer.creates.map(
    (c) =>
      ({
        // Only the fields the panel reads: a buffered create has no envelope
        // yet, so it renders unverified and un-approvable — which is exactly
        // what it is until Save mints the real row.
        id: c.tempId,
        kind: c.kind,
        verified_by: null,
        verified_at: null,
        approved_for_public: false,
        effective_from: null,
        effective_to: null,
        deleted_at: null,
        ...c.payload,
        isPending: true,
      }) as unknown as OfferRow,
  );

  return [...kept, ...added];
}

export interface UseOffersSaveBuffer {
  rows: OfferRow[];
  changeCount: number;
  /** Buffer one field edit. Setting a field back to its stored value clears it. */
  setField: (row: OfferRow, field: string, value: unknown) => void;
  /** Buffer several fields of one row at once (the legacy "Accept split" actions). */
  setFields: (row: OfferRow, patch: OfferPatch) => void;
  /** Buffer a new row. Starts blank, so it is visible but not yet a change. */
  addRow: (kind: OfferKind, payload?: OfferPatch) => void;
  /**
   * Single-row kinds (dispatch_fee, financing): merge into the pending create
   * for this kind if one exists, otherwise start it. Typing into an empty
   * "Amount" field must not mint a fresh row per keystroke, and two fields of
   * the same not-yet-created row must end up on ONE create.
   */
  addOrMergeSingleton: (kind: OfferKind, patch: OfferPatch) => void;
  /** Drop a buffered create outright; mark a stored row for soft-delete. */
  removeRow: (row: OfferRow) => void;
  save: () => Promise<DirtyFormSaveResult>;
  discard: () => void;
}

type UpsertEntry = (
  table: 'offers',
  payload: Record<string, unknown>,
  id: string | undefined,
  tenantId: string,
) => Promise<BbEntry<'offers'> | null>;
type DiscardEntry = (table: 'offers', id: string, tenantId: string) => Promise<void>;

export function useOffersSaveBuffer(
  offers: BbEntry<'offers'>[],
  upsertEntry: UpsertEntry,
  discardEntry: DiscardEntry,
  /** The tenant the panel rendered for; every write of a save loop names it. */
  bbTenant: string,
): UseOffersSaveBuffer {
  const [buffer, setBuffer] = useState<OffersBuffer>(EMPTY_OFFERS_BUFFER);

  // save() runs outside render and must see the latest buffer and the latest
  // stored rows — not whatever the closure captured when it was created.
  // Mirrored in an effect rather than assigned during render: React flushes
  // passive effects before it processes the next discrete event, so a Save
  // click always reads the state the operator was last shown.
  const bufferRef = useRef(buffer);
  const offersRef = useRef(offers);
  const tempIdRef = useRef(0);
  useEffect(() => {
    bufferRef.current = buffer;
    offersRef.current = offers;
  }, [buffer, offers]);

  const rows = useMemo(() => mergeRows(offers, buffer), [offers, buffer]);
  const changeCount = useMemo(() => countChanges(buffer), [buffer]);

  const setFields = useCallback((row: OfferRow, patch: OfferPatch) => {
    setBuffer((prev) => {
      if (row.isPending) {
        return {
          ...prev,
          creates: prev.creates.map((c) =>
            c.tempId === row.id ? { ...c, payload: { ...c.payload, ...patch } } : c,
          ),
        };
      }
      const stored = offersRef.current.find((e) => e.id === row.id);
      const next: OfferPatch = { ...(prev.patches[row.id] ?? {}) };
      for (const [field, value] of Object.entries(patch)) {
        if (sameValue(value, (stored as Record<string, unknown> | undefined)?.[field])) {
          delete next[field];
        } else {
          next[field] = value;
        }
      }
      const patches = { ...prev.patches };
      if (Object.keys(next).length === 0) delete patches[row.id];
      else patches[row.id] = next;
      return { ...prev, patches };
    });
  }, []);

  const setField = useCallback(
    (row: OfferRow, field: string, value: unknown) => setFields(row, { [field]: value }),
    [setFields],
  );

  const addRow = useCallback((kind: OfferKind, payload: OfferPatch = {}) => {
    tempIdRef.current += 1;
    const tempId = `new-${kind}-${tempIdRef.current}`;
    setBuffer((prev) => ({ ...prev, creates: [...prev.creates, { tempId, kind, payload }] }));
  }, []);

  const addOrMergeSingleton = useCallback((kind: OfferKind, patch: OfferPatch) => {
    setBuffer((prev) => {
      const existing = prev.creates.find((c) => c.kind === kind);
      if (existing) {
        return {
          ...prev,
          creates: prev.creates.map((c) =>
            c === existing ? { ...c, payload: { ...c.payload, ...patch } } : c,
          ),
        };
      }
      tempIdRef.current += 1;
      return {
        ...prev,
        creates: [...prev.creates, { tempId: `new-${kind}-${tempIdRef.current}`, kind, payload: patch }],
      };
    });
  }, []);

  const removeRow = useCallback((row: OfferRow) => {
    setBuffer((prev) => {
      if (row.isPending) {
        return { ...prev, creates: prev.creates.filter((c) => c.tempId !== row.id) };
      }
      if (prev.deletes.includes(row.id)) return prev;
      // Drop any buffered edits to a row being removed — committing both
      // would PATCH a row on its way to soft-delete for no reason.
      const patches = { ...prev.patches };
      delete patches[row.id];
      return { ...prev, patches, deletes: [...prev.deletes, row.id] };
    });
  }, []);

  const discard = useCallback(() => setBuffer(EMPTY_OFFERS_BUFFER), []);

  const save = useCallback(async (): Promise<DirtyFormSaveResult> => {
    const pending = bufferRef.current;
    const failedCreates: BufferedCreate[] = [];
    const failedPatches: Record<string, OfferPatch> = {};
    const failedDeletes: string[] = [];

    for (const create of pending.creates) {
      // Blank rows are UI scaffolding, not data — they were never a change
      // and they are not written. Dropping them here is what keeps a stray
      // "+ Add guarantee" click from minting a gate-satisfying empty row.
      if (isBlankPayload(create.payload)) continue;
      try {
        const created = await upsertEntry('offers', { kind: create.kind, ...create.payload }, undefined, bbTenant);
        // A null result with no throw is upsertEntry's no-tenant early return:
        // nothing was sent. Surface it as a failure rather than clearing the
        // buffer and reporting a save that never happened.
        if (!created) failedCreates.push(create);
      } catch {
        failedCreates.push(create);
      }
    }

    for (const [id, patch] of Object.entries(pending.patches)) {
      if (pending.deletes.includes(id)) continue;
      const stored = offersRef.current.find((e) => e.id === id);
      // The row went away underneath us (another tab, another operator).
      // Re-creating it from a stale patch would resurrect deleted content.
      if (!stored) continue;
      try {
        await upsertEntry('offers', { kind: stored.kind, ...patch }, id, bbTenant);
      } catch {
        failedPatches[id] = patch;
      }
    }

    for (const id of pending.deletes) {
      try {
        await discardEntry('offers', id, bbTenant);
      } catch {
        failedDeletes.push(id);
      }
    }

    const ok =
      failedCreates.length === 0 &&
      Object.keys(failedPatches).length === 0 &&
      failedDeletes.length === 0;

    // Only what failed stays dirty, so the footer count drops to what still
    // genuinely needs saving and a retry doesn't re-send the successful half.
    setBuffer({ patches: failedPatches, creates: failedCreates, deletes: failedDeletes });

    return ok ? true : { success: false, error: 'Some offer changes could not be saved.' };
  }, [upsertEntry, discardEntry, bbTenant]);

  return { rows, changeCount, setField, setFields, addRow, addOrMergeSingleton, removeRow, save, discard };
}
